/**
 * RESPONSIBILITY: read a cron expression into the sets of values it matches.
 *
 * Five fields — minute hour day-of-month month day-of-week — plus the `#` extension for
 * an nth weekday, because "the third Thursday of the month" is a real recurring event and
 * plain cron cannot say it.
 *
 *   0 10 * * 0        every Sunday at 10:00
 *   0 19 * * 5        every Friday at 19:00
 *   0 12 * * 4#3      the third Thursday, at 12:00
 *   30 8 1 * *        the 1st of every month, 08:30
 *
 * Hand-rolled rather than a dependency: this is the whole of what the events list needs,
 * and iCal recurrence is a far larger surface than anything here asks for.
 */

export interface CronExpr {
  minutes: number[];
  hours: number[];
  /** Empty means "any" — the same convention for every field. */
  daysOfMonth: number[];
  months: number[];
  daysOfWeek: number[];
  /** From `dow#n`: match only the nth such weekday in the month. */
  nth?: number;
}

const RANGES: Record<string, [number, number]> = {
  minutes: [0, 59], hours: [0, 23], daysOfMonth: [1, 31], months: [1, 12], daysOfWeek: [0, 6],
};

/** One field → the values it matches. Empty array means "any", never "none". */
function field(spec: string, [lo, hi]: [number, number]): number[] {
  if (spec === '*') return [];

  const out = new Set<number>();
  for (const part of spec.split(',')) {
    const [body, stepText] = part.split('/');
    const step = stepText ? Number(stepText) : 1;
    if (!Number.isInteger(step) || step < 1) throw new Error(`bad step in "${part}"`);

    let from = lo;
    let to = hi;
    if (body !== '*') {
      const [a, b] = body.split('-');
      from = Number(a);
      to = b === undefined ? Number(a) : Number(b);
    }
    if (!Number.isInteger(from) || !Number.isInteger(to) || from < lo || to > hi || from > to) {
      throw new Error(`"${part}" is outside ${lo}-${hi}`);
    }
    for (let v = from; v <= to; v += step) out.add(v);
  }
  return [...out].sort((a, b) => a - b);
}

/** Throws on anything it cannot read, so a bad expression is refused at the boundary. */
export function parseCron(text: string): CronExpr {
  const parts = text.trim().split(/\s+/);
  if (parts.length !== 5) throw new Error('a cron expression has five fields');

  // `4#3` — the third Thursday. Strip it before the day-of-week field is read.
  let nth: number | undefined;
  let dowSpec = parts[4];
  if (dowSpec.includes('#')) {
    const [dow, n] = dowSpec.split('#');
    nth = Number(n);
    if (!Number.isInteger(nth) || nth < 1 || nth > 5) throw new Error('#n must be 1-5');
    dowSpec = dow;
  }

  return {
    minutes: field(parts[0], RANGES.minutes),
    hours: field(parts[1], RANGES.hours),
    daysOfMonth: field(parts[2], RANGES.daysOfMonth),
    months: field(parts[3], RANGES.months),
    daysOfWeek: field(dowSpec, RANGES.daysOfWeek),
    nth,
  };
}

/** True when the expression is readable. For validating what an editor typed. */
export function isCron(text: string): boolean {
  try { parseCron(text); return true; } catch { return false; }
}
